import { Box, Button, Card, CardContent, Checkbox, FormHelperText, Grid, List, ListItem, ListItemText, Skeleton, Stack, Typography } from "@mui/material";
import { ListContextProvider, ReactAdminComponentPropsWithId, useEditContext, useGetList, useListContext, useRecordContext } from "ra-core"
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from 'yup'
import { Link as RouterLink } from 'react-router-dom'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { Content } from "@/components/content/Content"
import Edit from "@/controller/Edit"
import { useSimpleSaveAction } from "@/controller/SaveContextProvider"
import { RecPatch } from "@/dataProvider/tools"
import { OdResourcePageProps } from "@/types/resource"
import { decodeHashid } from "@/utils/hashid"
import { Datagrid } from "@/components/list/datagrid";
import Scrollbar from "@/components/Scrollbar";
import { FunctionField, TextField as DgTextField } from "@/components/field";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { DataAccessRoleResponseDto, PrimaryClassification, SecondaryClassificationResponseDto, UpdateDataAccessRoleInput } from "@/gen/api/governance";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import useLocalListController from "@/components/list/useLocalListController";
import { classificationListOptions } from "./DataClassifications";
import { useHasPermit } from "@/authProvider/useHasPermit";
import { MANAGE_DATA_ACCESS_ROLES } from "@/constants";
import { disabledColor } from "@/utils/a11y";
import { useResourceRegister } from "@/utils/resourceRegister";
import { UserNameResponseDto } from "@/gen/api/user";
import { pages, sorts } from "@/utils/paramsWithGetMainList";

const DataAccessRoleDetail: FC<ReactAdminComponentPropsWithId> = (props) => {
  useResourceRegister({ name: 'users-list-by-dar' })

  const { id: hId, location, ...rest } = props
  const id = decodeHashid(hId || '')
  const listCtrl = useLocalListController({ resource: 'settings/governance/data-classification', basePath: '', filter: { }, perPage: 10000 }, classificationListOptions)

  const dispatch = useDispatch()
  const goList = useCallback(() => dispatch(push({
    pathname: `/${props.resource}`,
    state: location?.state,
  })), [dispatch, props.resource, location?.state])
  const handlers = useDefaultSavedHandlers('Save Data Access Role')
  handlers.onSuccess.merge(goList)
  return (
    <ListContextProvider value={listCtrl}>
      <Edit {...({ id, ...rest} as any)} {...handlers} mutationMode="pessimistic" >
        <DataAccessRoleDetailBody {...rest} />
      </Edit>
    </ListContextProvider>
  )
}

type ClassificationDto = SecondaryClassificationResponseDto;

const validationSchema =
  Yup.object().shape({
    roleName: Yup.string().max(100, 'Role Name must be at most 100 characters').required('Role Name is required'),
    roleValue: Yup.string().max(100, 'Role Value must be at most 100 characters').required('Role Value is required').matches(/^\s*\S*\s*$/, 'Role Value should not contain white spaces'),
    description: Yup.string().max(512),
  })

const DataAccessRoleDetailBody: FC<OdResourcePageProps> = ({
  resource,
  options,
}) => {
  const hasWritePermit = useHasPermit([MANAGE_DATA_ACCESS_ROLES])
  const save = useSimpleSaveAction()
  const { record: preData, loaded } = useEditContext<DataAccessRoleResponseDto&RecPatch>()
  
  const initialValues = {
    submit: '',
    roleName: preData?.name || '',
    roleValue: preData?.value || '',
    description: preData?.description || '',
  }
  
  const preDataRef = useRef<boolean | null>(!!preData)
  const { data: userNameList } = useGetList<UserNameResponseDto & RecPatch>('users-list-by-dar', pages(), sorts(), { dataAccessRoleId: preData?.roleId || 0, name: preData?.value || '' })
  const userNames = Object.values(userNameList || {})
  
  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
  }) => {
    if (!hasWritePermit) { return }
    const options = {
      onSuccess: () => {
        setStatus({ success: true })
        setSubmitting(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    const roleGrants = Object.keys(grantMapRef.current).reduce((acc, k) => {
      const entry = grantMapRef.current[k]
      const clsId = Number.parseInt(k)
      if (entry.read)  { acc.readGrants .push(clsId) }
      if (entry.write) { acc.writeGrants.push(clsId) }
      return acc
    }, { readGrants: [] as number[], writeGrants: [] as number[] })

    const toSave: UpdateDataAccessRoleInput&RecPatch = {
      ...preData!,
      name: values.roleName,
      value: values.roleValue,
      description: values.description,
      ...roleGrants,
    }
    save(toSave, options)
  }, [save, hasWritePermit])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { errors, isSubmitting, submitForm, setValues } = formikCtrl

  useEffect(() => {
    if (!preDataRef.current && !!preData) {
      preDataRef.current = true
      setValues({
        roleName: preData.name || '',
        roleValue: preData.value || '',
        description: preData.description || '',
      } as any)
    }
  }, [preData, setValues])
  
  const listCtx = useListContext<ClassificationDto & RecPatch>()
  const { data, setSort } = listCtx
  const setSortRef = useRef(setSort)
  setSortRef.current = setSort

  const [grantUpdate, setGrantUpdate] = useState(0)
  const bumpVer = (n:number) => n + 1
  
  const {current:innerState} = useRef({
    allReadChecked: false, 
    readsIndeterminate: false, 
    allWriteChecked: false, 
    writeIndeterminate: false,
  })
  const prevStateSignature = useRef(JSON.stringify(innerState))
  const calculateInnerState = useCallback(() => {
    const values = Object.values(grantMapRef.current)
    innerState.allReadChecked  = values.every(v => v.read)
    innerState.allWriteChecked = values.every(v => v.write)
    innerState.readsIndeterminate = values.some(v => v.read) && values.some(v => !v.read)
    innerState.writeIndeterminate = values.some(v => v.write) && values.some(v => !v.write)
    const signature = JSON.stringify(innerState)
    if (signature !== prevStateSignature.current) {
      setTimeout(() => setSortRef.current('id', signature), 10)
    }
    prevStateSignature.current = signature
  }, [innerState])

  const grantMapRef = useRef<Record<string, {read:boolean,write:boolean}>>({})
  const dataLoaded = useRef(false)
  useEffect(() => {
    if (Object.keys(data).length > 0 && !!preData && !dataLoaded.current) {
      grantMapRef.current = Object.keys(data || {}).reduce((acc, k) => {
        const cls = data[k]
        acc[k] = { 
          read: (preData?.readGrants || []).some(x => x === cls.secondaryClassificationId),
          write: (preData?.writeGrants || []).some(x => x === cls.secondaryClassificationId),
        };
        return acc
      }, {} as Record<string, {read:boolean,write:boolean}>)
      calculateInnerState()
      setGrantUpdate(bumpVer)
      dataLoaded.current = true
    }
  }, [calculateInnerState, data, preData])


  const handleGrantChange = useCallback((d:ClassificationDto&RecPatch, grantName:'read'|'write') => (e:React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    grantMapRef.current[d.id][grantName] = checked
    if (grantName === 'write' && checked) {
      grantMapRef.current[d.id]['read'] = true
    }
    if (grantName === 'read' && !checked) {
      grantMapRef.current[d.id]['write'] = false
    }
    calculateInnerState()
    setGrantUpdate(bumpVer)
  }, [calculateInnerState])

  const handleSelectAll = (grantName: 'read'|'write') => (e:React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked
    if (grantName === 'read') {
      if (checked) {
        for(const v of Object.values(grantMapRef.current)) {
          v.read = true
        }
      } else {
        for(const dId of Object.keys(grantMapRef.current)) {
          const d = data && data[dId]
          if (!d || d.primaryClassification === PrimaryClassification.Public) {continue}
          const v = grantMapRef.current[dId]
          v.read = false
          v.write = false
        }
      }
    } else {
      if (checked) {
        for(const v of Object.values(grantMapRef.current)) {
          v.write = true
          v.read = true
        }
      } else {
        for(const v of Object.values(grantMapRef.current)) {
          v.write = false
        }
      }
    }
    calculateInnerState()
    setGrantUpdate(bumpVer)
  }

  const [showUsers, setShowUsers] = useState(false)
  const toggleShowUsers = () => {
    let showUsernames = false
    setShowUsers(v => {
      showUsernames = !v
      return showUsernames
    })
  }

  if (!preData) {
    return null
  }

  return (
    <Content 
      pageName="Data Access Role Detail" 
      mainActions={
        hasWritePermit &&
        <>
          <Button
            color="primary"
            disabled={isSubmitting}
            variant="contained"
            onClick={submitForm}
          >
            Save
          </Button>
          <Button
            color="primary"
            variant="outlined"
            component={RouterLink}
            to={`/${resource}`}
          >
            Cancel
          </Button>
        </>
      }
    >
      <OdForm formikCtrl={formikCtrl}>

        <Card>
          <CardContent>
            <Grid container spacing={4} 
              {...(!hasWritePermit && { sx: {
                '& input.Mui-disabled, & textarea.Mui-disabled': disabledColor,
              }})}
            >
              <Grid item xs={12} md={6} >
                <TextField label="Role Name*" name="roleName" disabled={!hasWritePermit} />
              </Grid>
              <Grid item xs={12} md={6} >
                <TextField label="Value*" name="roleValue" disabled={!hasWritePermit} />
              </Grid>
              <Grid item xs={12} md={12} >
                <TextField label="Description" name="description" multiline={true} disabled={!hasWritePermit} />
              </Grid>
              {Boolean(userNames && userNames.length > 0) &&
              <Grid item xs={12} md={12}>
                <Button variant="text" sx={{pt: 1, mb: -2.5, ml: 4}} onClick={toggleShowUsers} >{showUsers ? "Hide " : "Show "}User Assignments</Button>
              </Grid>}
              {Boolean(showUsers) &&
              <Grid item xs={12} md={12}>
                <List sx={{mr: 2, maxHeight: 180, overflowY: 'auto', pl: 5, mt: 1.2, mb: -2.5}}>
                  {userNames && userNames.map((r:any) => 
                  <ListItem key={r.name} disablePadding>
                    <ListItemText secondary={r.username} sx={{maxWidth: 700, wordBreak: 'keep-all', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} />
                  </ListItem>)}
                </List>
              </Grid>}
            </Grid>
            {errors.submit && (
              <Box sx={{ mt: 3 }}>
                <FormHelperText error>
                  {errors.submit}
                </FormHelperText>
              </Box>
            )}
          </CardContent>
        </Card>
            
        <Card sx={{ mt: 4 }}>
          <Scrollbar options={{ suppressScrollY: true }}>
            <Box sx={{ minWidth: 1150 }}>
              {!loaded ?
                <Box>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </Box> :
                <Datagrid 
                  hasBulkActions={false} 
                  sx={{ 
                    '& th' : { py: 2, '&:nth-of-type(2)': { p: 0 } },
                    '& td' : { py: 2, '&:nth-of-type(2)': { p: 0 } },
                    '& .name': { maxWidth: 150, display: 'inline-block', wordBreak: 'break-word' },
                    '& .description': { maxWidth: 500, display: 'inline-block', wordBreak: 'break-word' },
                  }}
                  {...listCtx}
                >
                  <FunctionField 
                      label={
                        <Stack direction="column" alignItems="center">
                          <span>Read Access</span>
                          <Checkbox 
                            disabled={!hasWritePermit}
                            checked={innerState.allReadChecked} 
                            indeterminate={innerState.readsIndeterminate} 
                            onChange={handleSelectAll('read')} 
                            inputProps={{ 'aria-label': 'Check All Read Grants' }}
                          />
                        </Stack>
                      }
                    sortable={false} 
                    render={(d?:ClassificationDto&RecPatch) => (
                      d ?
                      <Checkbox 
                        disabled={!hasWritePermit || d.primaryClassification === PrimaryClassification.Public}
                        checked={grantMapRef.current[d.id]?.read || false}
                        onChange={handleGrantChange(d, 'read')}
                        inputProps={{ 'aria-label': `Grant Read Access` }}
                      /> : null
                    )} 
                    textAlign="center"
                  />
                  <FunctionField 
                      label={
                        <Stack direction="column" alignItems="center">
                          <span>Write Access</span>
                          <Checkbox 
                            disabled={!hasWritePermit}
                            checked={innerState.allWriteChecked} 
                            indeterminate={innerState.writeIndeterminate} 
                            onChange={handleSelectAll('write')} 
                            inputProps={{ 'aria-label': 'Check All Write Grants' }}
                          />
                        </Stack>
                      }
                    sortable={false} 
                    render={(d?:ClassificationDto&RecPatch) => (
                      d ?
                      <Checkbox 
                        disabled={!hasWritePermit}
                        checked={grantMapRef.current[d.id]?.write || false}
                        onChange={handleGrantChange(d, 'write')}
                        inputProps={{ 'aria-label': `Grant Write Access` }}
                      /> : null
                    )} 
                    textAlign="center"
                  />
                  <DgTextField 
                    source="primaryClassificationText" 
                    label={
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Primary</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Classification</Typography>
                      </Box>
                    }
                    sortable={false}
                  />
                  <DgTextField 
                    source="name" 
                    label={
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Secondary</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }} component="p">Classification</Typography>
                      </Box>
                    }
                    sortable={false} 
                    className="name" 
                  />
                  <DgTextField source="description" label="Description" sortable={false} className="description" />
                </Datagrid>
              }
            </Box>
          </Scrollbar>
        </Card>

      </OdForm>
    </Content>
  )
}

export default DataAccessRoleDetail;
